import { BaseWebComponentDataProvider } from '../../../framework/BaseWebComponentDataProvider';
import { Column, ColumnKeys, CustomRulesWebComponentData, GridType, RuleType, Status } from '../types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, ViewType } from '../../../grid/gridView/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { Feature } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';
import { CompanySpecialValues } from '@nirvana/shared/dtos/types/shared/filters';

export class CustomRulesBaseDataProvider extends BaseWebComponentDataProvider {
  async getDataForView(): Promise<CustomRulesWebComponentData> {
    const displayedColumns = this.dependencies.isPartnerCompany()
      ? [
          Column.RULE_NAME,
          Column.CREATED_ON,
          Column.LAST_UPDATED_ON,
          Column.STATUS,
          Column.RULE_TYPE,
          Column.TAGS,
          Column.COMPANY,
          Column.INLINE_ACTIONS,
        ]
      : [
          Column.RULE_NAME,
          Column.CREATED_ON,
          Column.LAST_UPDATED_ON,
          Column.STATUS,
          Column.RULE_TYPE,
          Column.TAGS,
          Column.INLINE_ACTIONS,
        ];

    const filters: any[] = [
      {
        [ColumnKeys.RULE_NAME]: { contains: '' },
      },
      {
        [ColumnKeys.LAST_UPDATED_ON]: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        [ColumnKeys.STATUS]: { in: [Status.ENABLED, Status.DISABLED] },
      },
    ];

    if (this.dependencies.isPartnerCompany()) {
      const currentUser = this.dependencies.getCurrentUser();
      const activeCompany = this.dependencies.getActiveCompany();

      if (currentUser?.mdrSuperAnalyst && activeCompany?.companyId) {
        filters.push({ [ColumnKeys.COMPANY]: { in: [activeCompany.companyId] } });
      } else {
        filters.push({ [ColumnKeys.COMPANY]: { in: ['all'] } });
      }

    }

    const view = {
      id: 'custom-rules-grid',
      name: 'Custom rules grid',
      type: ViewType.DEFAULT,
      filters: filters,
      displayedColumns: displayedColumns,
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
      compactView: false,
    };

    return {
      views: [view],
      allColumns: this.getGridColumns(),
      defaultViewProps: view,
    };
  }

  protected getGridColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    const columns: any = [
      {
        column: Column.RULE_NAME,
        key: ColumnKeys.RULE_NAME,
        columnName: translateFn('Rule name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
      },
      {
        column: Column.CREATED_ON,
        key: ColumnKeys.CREATED_ON,
        columnName: translateFn('Created on'),
        type: ColumnType.DATE,
        removable: true,
        sortable: true,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: Column.LAST_UPDATED_ON,
        key: ColumnKeys.LAST_UPDATED_ON,
        columnName: translateFn('Last updated on'),
        type: ColumnType.DATE,
        removable: false,
        sortable: true,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName:
          this.dependencies.gridType === GridType.EXCLUSION ? translateFn('Status') : translateFn('On Access'),
        type: ColumnType.ENUM,
        values: this.getStatusColumnValues(),
        removable: false,
        sortable: true,
      },
      {
        column: Column.RULE_TYPE,
        key: ColumnKeys.RULE_TYPE,
        columnName: translateFn('Rule type'),
        type: ColumnType.ENUM,
        values: this.getRuleTypeColumnValues(),
        removable: true,
      },
      {
        column: Column.TAGS,
        key: ColumnKeys.TAGS,
        columnName: translateFn('Rule tags'),
        type: ColumnType.STRING,
        removable: true,
      },
      {
        column: Column.INLINE_ACTIONS,
        key: ColumnKeys.INLINE_ACTIONS,
        columnName: translateFn(''),
        type: ColumnType.ACTION,
        removable: false,
        width: '50px',
      },
    ];
    if (this.dependencies.isPartnerCompany()) {
      columns.push({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Companies'),
        type: ColumnType.COMPANY,
        removable: false,
        sortable: false,
        enumFilterCanSelectAll: true,
        showOnlySelectAll: true,
        showNone: true,
        filterResetValue: [CompanySpecialValues.ALL],
      });
    }

    return columns;
  }

  protected getStatusColumnValues() {
    return [
      {
        label: translateFn('Enabled'),
        value: Status.ENABLED,
      },
      {
        label: translateFn('Disabled'),
        value: Status.DISABLED,
      },
    ];
  }

  protected getRuleTypeColumnValues() {
    const result = [
      {
        label: translateFn('Basic'),
        value: RuleType.BASIC,
      },
    ];

    if (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_YARA_DETECTION_RULES]) &&
      this.dependencies.gridType === GridType.DETECTION
    ) {
      result.push({
        label: translateFn('YARA'),
        value: RuleType.YARA,
      });
    }

    return result;
  }
}
